import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, Trash2, X, Eye, Save } from 'lucide-react';
import { ImageUpload, API_URL } from './shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const HomepageTab = ({ token }) => {
  const [availableProducts, setAvailableProducts] = useState([]);
  const [savingHomepage, setSavingHomepage] = useState(false);
  const [heroForm, setHeroForm] = useState({
    headline: 'Your phone is all the underwater camera you need',
    subheading: 'Professional underwater photography gear for every diver',
    cta_text: 'Shop Now', cta_link: '/products', images: []
  });
  const [featuredProductIds, setFeaturedProductIds] = useState([]);
  const [categoriesForm, setCategoriesForm] = useState([]);
  const [divevolkForm, setDivevolkForm] = useState({
    title: 'Turn your phone into an underwater camera',
    subtitle: 'Premium underwater housings for your smartphone',
    cta_text: 'Explore Divevolk', cta_link: '/brand/divevolk', product_ids: []
  });
  const [trustForm, setTrustForm] = useState([]);
  const [socialForm, setSocialForm] = useState({ title: 'What Our Divers Say', images: [], testimonials: [] });

  useEffect(() => {
    fetchHomepageData();
  }, []);

  const fetchHomepageData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/homepage`, { headers: { Authorization: `Bearer ${token}` } });
      const sections = res.data.sections || {};
      setAvailableProducts(res.data.available_products || []);
      if (sections.hero) setHeroForm(prev => ({ ...prev, ...sections.hero }));
      if (sections.featured_products) setFeaturedProductIds(sections.featured_products.product_ids || []);
      if (sections.categories?.items) setCategoriesForm(sections.categories.items);
      if (sections.divevolk) setDivevolkForm(prev => ({ ...prev, ...sections.divevolk }));
      if (sections.trust?.items) setTrustForm(sections.trust.items);
      if (sections.social) setSocialForm(prev => ({ ...prev, ...sections.social }));
    } catch (error) {
      toast.error('Failed to load homepage data');
    }
  };

  const handleSaveAll = async () => {
    setSavingHomepage(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await Promise.all([
        axios.put(`${API_URL}/admin/homepage/hero`, heroForm, { headers }),
        axios.put(`${API_URL}/admin/homepage/featured_products`, { product_ids: featuredProductIds }, { headers }),
        axios.put(`${API_URL}/admin/homepage/categories`, { items: categoriesForm }, { headers }),
        axios.put(`${API_URL}/admin/homepage/divevolk`, divevolkForm, { headers }),
        axios.put(`${API_URL}/admin/homepage/trust`, { items: trustForm }, { headers }),
        axios.put(`${API_URL}/admin/homepage/social`, socialForm, { headers })
      ]);
      toast.success('Homepage saved successfully!');
    } catch (error) {
      toast.error('Failed to save homepage');
    } finally {
      setSavingHomepage(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Homepage Editor</h2>
          <p className="text-sm text-gray-500">Customize all sections of your homepage without code</p>
        </div>
        <Button onClick={handleSaveAll} disabled={savingHomepage} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="save-homepage-btn">
          {savingHomepage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      {/* Hero Section Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-2 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">1</span>
          Hero Section (Slide 1)
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Edits below control the <strong>first hero slide</strong> on the homepage (headline, subheading and CTA button).
          The 4 hero <strong>images</strong> rotate as backgrounds across all slides.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
            <Input value={heroForm.headline} onChange={(e) => setHeroForm({...heroForm, headline: e.target.value})} placeholder="Your phone is all the underwater camera you need" data-testid="hero-headline-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subheading</label>
            <Input value={heroForm.subheading} onChange={(e) => setHeroForm({...heroForm, subheading: e.target.value})} placeholder="Professional underwater photography gear for every diver" data-testid="hero-subheading-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
              <Input value={heroForm.cta_text} onChange={(e) => setHeroForm({...heroForm, cta_text: e.target.value})} placeholder="Shop Now" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Link</label>
              <Input value={heroForm.cta_link} onChange={(e) => setHeroForm({...heroForm, cta_link: e.target.value})} placeholder="/products" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hero Images (Rotating Background)</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(heroForm.images || []).map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img} alt={`Hero ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
                  <button onClick={() => setHeroForm({...heroForm, images: heroForm.images.filter((_, i) => i !== idx)})} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div>
                <ImageUpload value="" onChange={(url) => { if (url) setHeroForm({...heroForm, images: [...(heroForm.images || []), url]}); }} token={token} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">2</span>
          Featured Products Carousel
        </h3>
        <p className="text-sm text-gray-500 mb-4">Select products to display in the featured carousel. No prices shown.</p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Selected Products ({featuredProductIds.length})</label>
          <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] p-2 border rounded-lg bg-gray-50">
            {featuredProductIds.map((pid) => {
              const product = availableProducts.find(p => p.id === pid);
              return (
                <Badge key={pid} variant="secondary" className="flex items-center gap-1">
                  {product?.name?.slice(0, 25) || pid}
                  <button onClick={() => setFeaturedProductIds(featuredProductIds.filter(id => id !== pid))}><X className="w-3 h-3" /></button>
                </Badge>
              );
            })}
            {featuredProductIds.length === 0 && <span className="text-sm text-gray-400">No products selected - defaults will be used</span>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Available Products</label>
          <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-1">
            {availableProducts.filter(p => !featuredProductIds.includes(p.id)).slice(0, 50).map(product => (
              <button key={product.id} onClick={() => setFeaturedProductIds([...featuredProductIds, product.id])} className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-3">
                <img src={product.image_url || '/placeholder.jpg'} alt="" className="w-10 h-10 object-cover rounded" />
                <span className="text-sm">{product.name}</span>
                <Plus className="w-4 h-4 ml-auto text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Shop by Category Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">3</span>
          Shop by Category
        </h3>
        <p className="text-sm text-gray-500 mb-4">Add category tiles with images that link to category pages.</p>
        <div className="space-y-4">
          {categoriesForm.map((cat, idx) => (
            <div key={cat.id || idx} className="flex items-center gap-4 p-3 border rounded-lg bg-gray-50">
              <div className="w-16 h-16">
                <ImageUpload value={cat.image_url} onChange={(url) => { const updated = [...categoriesForm]; updated[idx].image_url = url; setCategoriesForm(updated); }} token={token} />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input value={cat.title} onChange={(e) => { const updated = [...categoriesForm]; updated[idx].title = e.target.value; setCategoriesForm(updated); }} placeholder="Category Title" />
                <Input value={cat.link} onChange={(e) => { const updated = [...categoriesForm]; updated[idx].link = e.target.value; setCategoriesForm(updated); }} placeholder="/products?category=masks" />
              </div>
              <Button variant="ghost" size="icon" onClick={() => setCategoriesForm(categoriesForm.filter((_, i) => i !== idx))}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => setCategoriesForm([...categoriesForm, { id: `cat-${Date.now()}`, title: '', image_url: '', link: '', order: categoriesForm.length }])}>
            <Plus className="w-4 h-4 mr-2" /> Add Category
          </Button>
        </div>
      </div>

      {/* DiveVolk Section Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">4</span>
          DiveVolk Feature Section
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
            <Input value={divevolkForm.title} onChange={(e) => setDivevolkForm({...divevolkForm, title: e.target.value})} placeholder="Turn your phone into an underwater camera" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <Input value={divevolkForm.subtitle} onChange={(e) => setDivevolkForm({...divevolkForm, subtitle: e.target.value})} placeholder="Premium underwater housings for your smartphone" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
              <Input value={divevolkForm.cta_text} onChange={(e) => setDivevolkForm({...divevolkForm, cta_text: e.target.value})} placeholder="Explore Divevolk" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
              <Input value={divevolkForm.cta_link} onChange={(e) => setDivevolkForm({...divevolkForm, cta_link: e.target.value})} placeholder="/brand/divevolk" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">DiveVolk Products to Feature</label>
            <p className="text-xs text-gray-500 mb-2">Select which DiveVolk products appear. Leave empty for auto-selection.</p>
            <div className="flex flex-wrap gap-2 mb-4 min-h-[40px] p-2 border rounded-lg bg-gray-50">
              {(divevolkForm.product_ids || []).map((pid) => {
                const product = availableProducts.find(p => p.id === pid);
                return (
                  <Badge key={pid} variant="secondary" className="flex items-center gap-1">
                    {product?.name?.slice(0, 25) || pid}
                    <button onClick={() => setDivevolkForm({...divevolkForm, product_ids: divevolkForm.product_ids.filter(id => id !== pid)})}><X className="w-3 h-3" /></button>
                  </Badge>
                );
              })}
            </div>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
              {availableProducts.filter(p => p.category === 'brand_product' && !(divevolkForm.product_ids || []).includes(p.id)).map(product => (
                <button key={product.id} onClick={() => setDivevolkForm({...divevolkForm, product_ids: [...(divevolkForm.product_ids || []), product.id]})} className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded flex items-center gap-3">
                  <img src={product.image_url || '/placeholder.jpg'} alt="" className="w-8 h-8 object-cover rounded" />
                  <span className="text-sm">{product.name}</span>
                  <Plus className="w-4 h-4 ml-auto text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Why Just Dive / Trust Strip Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">5</span>
          Why Just Dive (Trust Strip)
        </h3>
        <p className="text-sm text-gray-500 mb-4">Add trust-building items with icons and descriptions.</p>
        <div className="space-y-4">
          {trustForm.map((item, idx) => (
            <div key={item.id || idx} className="flex items-start gap-4 p-3 border rounded-lg bg-gray-50">
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Icon</label>
                <Select value={item.icon} onValueChange={(val) => { const updated = [...trustForm]; updated[idx].icon = val; setTrustForm(updated); }}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Icon" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shield">Shield</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="headphones">Support</SelectItem>
                    <SelectItem value="award">Award</SelectItem>
                    <SelectItem value="star">Star</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-2">
                <Input value={item.title} onChange={(e) => { const updated = [...trustForm]; updated[idx].title = e.target.value; setTrustForm(updated); }} placeholder="Title" />
                <Input value={item.description} onChange={(e) => { const updated = [...trustForm]; updated[idx].description = e.target.value; setTrustForm(updated); }} placeholder="Description" />
              </div>
              <Button variant="ghost" size="icon" onClick={() => setTrustForm(trustForm.filter((_, i) => i !== idx))}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => setTrustForm([...trustForm, { id: `trust-${Date.now()}`, icon: 'check', title: '', description: '', order: trustForm.length }])}>
            <Plus className="w-4 h-4 mr-2" /> Add Trust Item
          </Button>
        </div>
      </div>

      {/* Social Proof / Testimonials Editor */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
          <span className="w-8 h-8 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-sm">6</span>
          Social Proof & Testimonials
        </h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
            <Input value={socialForm.title} onChange={(e) => setSocialForm({...socialForm, title: e.target.value})} placeholder="What Our Divers Say" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Images</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(socialForm.images || []).map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img} alt={`Social ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
                  <button onClick={() => setSocialForm({...socialForm, images: socialForm.images.filter((_, i) => i !== idx)})} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <div>
                <ImageUpload value="" onChange={(url) => { if (url) setSocialForm({...socialForm, images: [...(socialForm.images || []), url]}); }} token={token} />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Testimonials</label>
            <div className="space-y-4">
              {(socialForm.testimonials || []).map((test, idx) => (
                <div key={test.id || idx} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                  <div className="flex gap-4">
                    <Input value={test.author} onChange={(e) => { const updated = [...(socialForm.testimonials || [])]; updated[idx].author = e.target.value; setSocialForm({...socialForm, testimonials: updated}); }} placeholder="Author name" className="w-1/3" />
                    <Select value={String(test.rating || 5)} onValueChange={(val) => { const updated = [...(socialForm.testimonials || [])]; updated[idx].rating = parseInt(val); setSocialForm({...socialForm, testimonials: updated}); }}>
                      <SelectTrigger className="w-24"><SelectValue placeholder="Rating" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Stars</SelectItem>
                        <SelectItem value="4">4 Stars</SelectItem>
                        <SelectItem value="3">3 Stars</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => setSocialForm({...socialForm, testimonials: socialForm.testimonials.filter((_, i) => i !== idx)})}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  <textarea value={test.text} onChange={(e) => { const updated = [...(socialForm.testimonials || [])]; updated[idx].text = e.target.value; setSocialForm({...socialForm, testimonials: updated}); }} placeholder="Testimonial text..." className="w-full p-2 border rounded-lg text-sm" rows={2} />
                </div>
              ))}
              <Button variant="outline" onClick={() => setSocialForm({...socialForm, testimonials: [...(socialForm.testimonials || []), { id: `test-${Date.now()}`, author: '', text: '', rating: 5, order: (socialForm.testimonials || []).length }]})}>
                <Plus className="w-4 h-4 mr-2" /> Add Testimonial
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Link */}
      <div className="text-center">
        <a href="/" target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline inline-flex items-center gap-2">
          <Eye className="w-4 h-4" /> Preview Homepage
        </a>
      </div>
    </div>
  );
};

export default HomepageTab;
